import React from "react";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, AlertTriangle, ShieldAlert, CheckCircle2, Search } from "lucide-react";
import moment from "moment";

const INCIDENT_TYPE_LABELS = {
  data_breach: "Filtració de dades",
  unauthorized_access: "Accés no autoritzat",
  data_loss: "Pèrdua de dades",
  data_corruption: "Corrupció de dades",
  accidental_disclosure: "Divulgació accidental",
  other: "Altre",
};

const RISK_CONFIG = {
  low: { label: "Risc baix", badge: "bg-blue-100 text-blue-700", border: "border-l-blue-300" },
  medium: { label: "Risc mitjà", badge: "bg-amber-100 text-amber-700", border: "border-l-amber-400" },
  high: { label: "Risc alt", badge: "bg-orange-100 text-orange-700", border: "border-l-orange-500" },
  critical: { label: "Risc crític", badge: "bg-red-100 text-red-700", border: "border-l-red-600" },
};

const STATUS_CONFIG = {
  open: { label: "Oberta", badge: "bg-red-100 text-red-700" },
  investigating: { label: "En investigació", badge: "bg-amber-100 text-amber-700" },
  resolved: { label: "Resolta", badge: "bg-emerald-100 text-emerald-700" },
};

export default function DataIncidentsList({ items, onEdit, onDeleted, isAdmin }) {
  const handleDelete = async (id) => {
    await hosys.entities.DataIncident.delete(id);
    onDeleted(id);
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <ShieldAlert className="w-10 h-10 mx-auto mb-2 text-gray-200" />
        <p className="text-sm">Sense incidències registrades</p>
        <p className="text-xs text-gray-300 mt-1">Bon senyal! Registra qualsevol incident de seguretat de dades.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map(item => {
        const rc = RISK_CONFIG[item.risk_level] || RISK_CONFIG.medium;
        const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.open;
        const needsAEPD = (item.risk_level === "high" || item.risk_level === "critical") && !item.notified_authority && item.status !== "resolved";
        const daysFromDiscovery = moment().diff(moment(item.discovery_date), "days");

        return (
          <Card key={item.id} className={`p-4 border-0 shadow-sm border-l-4 ${rc.border}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                item.risk_level === "critical" ? "text-red-600" :
                item.risk_level === "high" ? "text-orange-500" :
                item.risk_level === "medium" ? "text-amber-500" : "text-blue-500"
              }`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4>
                  <div className="flex gap-1.5 flex-wrap">
                    <Badge className={`${rc.badge} border-0 text-[10px]`}>{rc.label}</Badge>
                    <Badge className={`${sc.badge} border-0 text-[10px]`}>{sc.label}</Badge>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{INCIDENT_TYPE_LABELS[item.incident_type]}</p>
                {item.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{item.description}</p>}

                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-gray-400">
                  <span>Descoberta: {moment(item.discovery_date).format("DD/MM/YYYY")}</span>
                  {item.affected_subjects_count > 0 && <span>· {item.affected_subjects_count} afectats</span>}
                  {item.responsible_name && <span>· {item.responsible_name}</span>}
                  <span className={daysFromDiscovery > 7 && item.status === "open" ? "text-red-500 font-semibold" : ""}>
                    · Fa {daysFromDiscovery} dies
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mt-1.5">
                  {item.notified_authority && (
                    <span className="text-[10px] bg-blue-50 text-blue-600 rounded-full px-2 py-0.5 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> AEPD notificada
                    </span>
                  )}
                  {item.notified_subjects && (
                    <span className="text-[10px] bg-emerald-50 text-emerald-600 rounded-full px-2 py-0.5 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Afectats notificats
                    </span>
                  )}
                  {needsAEPD && (
                    <span className="text-[10px] bg-red-50 text-red-600 border border-red-200 rounded-full px-2 py-0.5 font-semibold animate-pulse-subtle">
                      ⚠ Cal notificar l'AEPD!
                    </span>
                  )}
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-[#7B2D3F]" onClick={() => onEdit(item)}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-red-600" onClick={() => handleDelete(item.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}